import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchSchemas } from '../../services/schemasService';
import { inferHasMore } from '../../utilities/flow/schemaEventHelpers';

// The no-op filter set: empty arrays / blank strings ⇒ backend applies no filter.
export const EMPTY_FILTERS = {
  priorities: [],
  eventTypes: [],
  searchEventName: '',
  searchEventTitle: '',
};

// Fetch the first page for a (possibly new) filter set. Replaces the list.
export const fetchSchemasAsync = createAsyncThunk(
  'schemas/fetch',
  async ({ filters = EMPTY_FILTERS } = {}, { getState }) => {
    const { limit } = getState().schemas;
    return await fetchSchemas({ filters, limit, offset: 0 });
  },
);

// Fetch the next page for the current filter set. Appends to the list.
export const fetchMoreSchemasAsync = createAsyncThunk(
  'schemas/fetchMore',
  async (_, { getState }) => {
    const { filters, limit, offset } = getState().schemas;
    return await fetchSchemas({ filters, limit, offset });
  },
);

const schemasSlice = createSlice({
  name: 'schemas',
  initialState: {
    items: [],
    loading: false,
    loadingMore: false,
    error: null,
    filters: EMPTY_FILTERS,
    limit: 25,
    offset: 0,
    hasMore: true,
  },
  reducers: {
    clearSchemas: (state) => {
      state.items = [];
      state.offset = 0;
      state.hasMore = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchemasAsync.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.filters = action.meta.arg?.filters ?? EMPTY_FILTERS;
        state.offset = 0;
      })
      .addCase(fetchSchemasAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.offset = action.payload.length;
        state.hasMore = inferHasMore(action.payload.length, state.limit);
      })
      .addCase(fetchSchemasAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchMoreSchemasAsync.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(fetchMoreSchemasAsync.fulfilled, (state, action) => {
        state.loadingMore = false;
        state.items = [...state.items, ...action.payload];
        state.offset += action.payload.length;
        state.hasMore = inferHasMore(action.payload.length, state.limit);
      })
      .addCase(fetchMoreSchemasAsync.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.error.message;
      });
  },
});

export const { clearSchemas } = schemasSlice.actions;

export const selectSchemas = (state) => state.schemas.items;
export const selectSchemasLoading = (state) => state.schemas.loading;
export const selectSchemasLoadingMore = (state) => state.schemas.loadingMore;
export const selectSchemasError = (state) => state.schemas.error;
export const selectSchemasHasMore = (state) => state.schemas.hasMore;
export const selectSchemasFilters = (state) => state.schemas.filters;

export default schemasSlice.reducer;
